import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../components/Orchestrio";

const ProcessPage = () => {
  const phases = [
    {
      number: "01",
      title: "Discovery",
      duration: "1-2 weeks",
      description: "We start by understanding your business, your users, and your goals. No assumptions. No jumping straight to solutions.",
      activities: [
        "Stakeholder interviews",
        "Requirements gathering",
        "Technical assessment",
        "Existing system review"
      ],
      deliverables: [
        "Project scope document",
        "Technical specification",
        "Timeline and milestones",
        "Fixed-price quote"
      ]
    },
    {
      number: "02",
      title: "Design",
      duration: "2-4 weeks",
      description: "We design the system architecture and user interface. You'll see working prototypes before we write production code.",
      activities: [
        "Information architecture",
        "Wireframes",
        "Visual design",
        "Database schema design"
      ],
      deliverables: [
        "UI/UX designs (Figma)",
        "Technical architecture",
        "Component specifications",
        "Sign-off document"
      ]
    },
    {
      number: "03",
      title: "Build",
      duration: "4-12 weeks",
      description: "Development in 2-week sprints with regular demos. You'll see progress every week and can adjust priorities as we go.",
      activities: [
        "Frontend development",
        "Backend development",
        "API integrations",
        "Testing (unit, integration)"
      ],
      deliverables: [
        "Weekly progress updates",
        "Demo environments",
        "Code in version control",
        "Test documentation"
      ]
    },
    {
      number: "04",
      title: "Launch",
      duration: "1-2 weeks",
      description: "Thorough testing, client sign-off, and a managed deployment. We don't launch until you're confident everything works.",
      activities: [
        "User acceptance testing",
        "Performance testing",
        "Security review",
        "Production deployment"
      ],
      deliverables: [
        "Production system",
        "User documentation",
        "Technical documentation",
        "Training session"
      ]
    },
    {
      number: "05",
      title: "Support",
      duration: "Ongoing",
      description: "Post-launch support to fix any issues and help you get the most from your new system. Optional ongoing maintenance available.",
      activities: [
        "Bug fixes",
        "Performance monitoring",
        "Security updates",
        "Feature requests"
      ],
      deliverables: [
        "30-day warranty period",
        "Support agreement (optional)",
        "Quarterly reviews",
        "Improvement roadmap"
      ]
    }
  ];

  const principles = [
    {
      title: "Fixed-price quotes",
      description: "After discovery, you get a fixed price. If we underestimate, that's on us—not you."
    },
    {
      title: "Weekly updates",
      description: "Every Friday, you get a progress update. No surprises, no radio silence."
    },
    {
      title: "Working demos",
      description: "You'll see your system working in a staging environment throughout development."
    },
    {
      title: "No scope creep",
      description: "Changes are welcome, but we'll discuss the impact on timeline and cost upfront."
    },
    {
      title: "Everything documented",
      description: "Technical docs, user guides, architecture diagrams. All yours."
    },
    {
      title: "Clean handover",
      description: "Full access to code, hosting, and accounts. No lock-in."
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="process-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            Process
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            How we work
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            A structured process that delivers predictable results. 
            You'll know what's happening, what's next, and what it will cost—at every stage.
          </p>
        </div>
      </section>

      {/* Phases */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-16">
            {phases.map((phase, index) => (
              <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Phase header */}
                <div className="lg:col-span-4">
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="text-4xl font-bold text-[#2F5D8C]">{phase.number}</span>
                    <span className="text-sm text-[#6B7280]">{phase.duration}</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-3">{phase.title}</h2>
                  <p className="text-[#9CA3AF] leading-relaxed">{phase.description}</p>
                </div>

                {/* Phase details */}
                <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
                    <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">
                      What we do
                    </h3>
                    <ul className="space-y-3">
                      {phase.activities.map((activity, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                          <span className="w-1.5 h-1.5 bg-[#5F8FB8] rounded-full flex-shrink-0" />
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
                    <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">
                      What you get
                    </h3>
                    <ul className="space-y-3">
                      {phase.deliverables.map((deliverable, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                          <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Our commitments</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <div key={index}>
                <h3 className="font-semibold mb-2">{principle.title}</h3>
                <p className="text-sm text-[#9CA3AF] leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Ready to start?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Book a discovery call to discuss your project. We'll give you an honest assessment 
                of whether we're the right fit.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProcessPage;
